import { api } from './client';

// ── DTOs ──

export interface ParsedBankOperationDto {
  rowIndex: number;
  date: string;
  documentNumber: string;
  amount: number;
  currencyCode: string;
  description: string;
  counterpartyName: string;
  counterpartyCode: string;
  counterpartyAccount: string;
  bankReference: string | null;
}

export type MappingConfidence = 'High' | 'Medium' | 'Low';

export interface ImportMappingSuggestion {
  rowIndex: number;
  suggestedPersonId: number | null;
  suggestedPersonName: string | null;
  suggestedDebitAccount: number | null;
  suggestedCreditAccount: number | null;
  confidence: MappingConfidence;
  matchReason: string;
  linkedInvoiceId: number | null;
  linkedInvoiceInfo: string | null;
}

/** Statement-level metadata (camt.052/053 + ESIS): own IBAN, period, balances. */
export interface StatementInfoDto {
  accountIban: string | null;
  accountCurrency: string | null;
  bankName: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  balanceStart: number | null;
  balanceEnd: number | null;
}

export interface ParseBankStatementResultDto {
  detectedFormat: string;
  operations: ParsedBankOperationDto[];
  errors: string[];
  statementInfo?: StatementInfoDto | null;
}

export interface ParseAndMatchResultDto {
  detectedFormat: string;
  operations: ParsedBankOperationDto[];
  suggestions: ImportMappingSuggestion[];
  errors: string[];
}

export interface ConfirmImportResultDto {
  createdCount: number;
  skippedCount: number;
  errors: string[];
}

export interface ConfirmedImportOperation {
  date: string;
  documentNumber: string;
  amount: number;
  currencyCode: string;
  description: string;
  counterpartyName: string;
  counterpartyAccount: string;
  debitAccount: number;
  creditAccount: number;
  personId: number | null;
  bankReference: string | null;
}

export interface ConfirmImportRequest {
  bankAccountCode: string;
  operations: ConfirmedImportOperation[];
}

// ── Legacy (kept for backward compatibility) ──

export interface BankImportResultDto {
  importedCount: number;
  skippedCount: number;
  errors: string[];
}

export interface ImportBankStatementRequest {
  bankCode: string;
  csvContent: string;
  defaultDebitAccount: number;
  defaultCreditAccount: number;
  defaultPersonId?: number | null;
}

// ── API Client ──

export const bankImportApi = {
  /** Legacy CSV import -- creates operations immediately */
  import: (data: ImportBankStatementRequest) =>
    api.post<BankImportResultDto>('/bank-import', data),

  /** Parse file content (text) without creating records */
  parse: (data: { content: string; fileName?: string; bankCode?: string }) =>
    api.post<ParseBankStatementResultDto>('/bank-import/parse', data),

  /** Upload file as multipart form data */
  upload: (file: File, bankCode?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    if (bankCode) formData.append('bankCode', bankCode);
    return api.upload<ParseBankStatementResultDto>('/bank-import/upload', formData);
  },

  /** Get mapping suggestions for parsed operations */
  match: (data: { detectedFormat?: string; operations: ParsedBankOperationDto[] }) =>
    api.post<ParseAndMatchResultDto>('/bank-import/match', data),

  /** Parse + match in one call */
  parseAndMatch: (data: { content: string; fileName?: string; bankCode?: string }) =>
    api.post<ParseAndMatchResultDto>('/bank-import/parse-and-match', data),

  /** Confirm and persist batch of reviewed operations */
  confirm: (data: ConfirmImportRequest) =>
    api.post<ConfirmImportResultDto>('/bank-import/confirm', data),
};
